import { blockFinishedInches, blockPieces, findFabricByGroupSlot, sortedGroups, stripPlan, type CutBatch } from "./geometry"
import type { Fabric, Project } from "./state"

export interface FabricCapacity {
  fabric: string
  slot: string
  haveYards: number
  maxBlocksPerGroup: number
  inchesUsed: number
}

export interface SplitOption {
  babyN: number
  babyLayout: [number, number]
  mainN: number
  mainLayout: [number, number]
}

export interface ExtraNeeded {
  babyN: number
  babyLayout: [number, number]
  extraYardsPerGroup: number
  extraYardsTotal: number
}

export interface CapacityReport {
  numGroups: number
  refGroup: string
  blockWidthInches: number
  blockHeightInches: number
  capacities: FabricCapacity[]
  constraining: FabricCapacity
  maxTotal: number
  singleLayouts: [number, number][]
  splits: SplitOption[]
  extraNeeded: ExtraNeeded[]
}

export function analyzeCapacity(project: Project): CapacityReport {
  const groups = sortedGroups(project)
  if (groups.length === 0) throw new Error("No colorway groups defined.")
  const numGroups = groups.length
  const refGroup = groups[0]

  // Slot fabrics only — structural roles (binding/backing/setting/sashing/
  // cornerstone) don't participate in per-block capacity math; they're
  // budgeted as aggregates.
  const refFabrics = project.fabrics.filter(
    (f) => f.group === refGroup && f.slot !== null
  )
  const capacities = refFabrics.map((f) => fabricCapacity(project, f))

  const constraining = capacities.reduce((a, b) =>
    a.maxBlocksPerGroup <= b.maxBlocksPerGroup ? a : b
  )
  const maxTotal = constraining.maxBlocksPerGroup * numGroups

  const singleLayouts = allLayouts(maxTotal)

  const splits: SplitOption[] = []
  for (let babyN = numGroups; babyN <= Math.floor(maxTotal / 2); babyN += numGroups) {
    const mainN = maxTotal - babyN
    const bl = bestLayout(babyN)
    const ml = bestLayout(mainN)
    if (bl && ml) splits.push({ babyN, babyLayout: bl, mainN, mainLayout: ml })
  }

  const extraNeeded: ExtraNeeded[] = []
  const upper = Math.min(Math.floor(maxTotal / 3), 40)
  for (let babyN = numGroups; babyN <= upper; babyN += numGroups) {
    const bl = bestLayout(babyN)
    if (!bl) continue
    const newPerGroup = constraining.maxBlocksPerGroup + Math.floor(babyN / numGroups)
    const constrainingFabric = findFabricByGroupSlot(project, refGroup, constraining.slot)
    if (!constrainingFabric) continue
    const newInches = stripInches(project, constrainingFabric, newPerGroup)
    const extraPerGroup = Math.max(0, newInches - constrainingFabric.yards * 36.0)
    extraNeeded.push({
      babyN,
      babyLayout: bl,
      extraYardsPerGroup: Math.round((extraPerGroup / 36.0) * 1000) / 1000,
      extraYardsTotal: Math.round(((extraPerGroup * numGroups) / 36.0) * 1000) / 1000,
    })
  }

  const [blockW, blockH] = blockFinishedInches(project.block)
  return {
    numGroups,
    refGroup,
    blockWidthInches: blockW,
    blockHeightInches: blockH,
    capacities,
    constraining,
    maxTotal,
    singleLayouts,
    splits,
    extraNeeded,
  }
}

function stripInches(project: Project, fabric: Fabric, n: number): number {
  const pieces = blockPieces(project.block)
  const pieceRoles = project.block.piece_roles
  const relevant = pieces.filter((p) => (pieceRoles[p.patchName] ?? p.patchName) === fabric.slot)
  return relevant.reduce((sum, p) => {
    const batch: CutBatch = { fabric: fabric.name, patchName: p.patchName, size: p.size, length: p.length, count: p.count * n }
    return sum + stripPlan(batch, fabric.width).inchesUsed
  }, 0)
}

function fabricCapacity(project: Project, fabric: Fabric): FabricCapacity {
  const have = fabric.yards * 36.0
  let prev = 0
  for (let n = 1; n <= 1000; n++) {
    const used = stripInches(project, fabric, n)
    if (used > have) {
      return { fabric: fabric.name, slot: fabric.slot ?? "", haveYards: fabric.yards, maxBlocksPerGroup: n - 1, inchesUsed: prev }
    }
    prev = used
  }
  return { fabric: fabric.name, slot: fabric.slot ?? "", haveYards: fabric.yards, maxBlocksPerGroup: 1000, inchesUsed: prev }
}

function allLayouts(n: number): [number, number][] {
  const seen = new Set<string>()
  const result: [number, number][] = []
  for (let cols = 1; cols <= n; cols++) {
    if (n % cols !== 0) continue
    const rows = n / cols
    const pair: [number, number] = [Math.max(cols, rows), Math.min(cols, rows)]
    const key = `${pair[0]},${pair[1]}`
    if (seen.has(key)) continue
    seen.add(key)
    if (pair[0] / pair[1] <= 2.5) result.push(pair)
  }
  result.sort((a, b) => a[0] / a[1] - b[0] / b[1])
  return result
}

function bestLayout(n: number): [number, number] | null {
  const layouts = allLayouts(n)
  return layouts[0] ?? null
}
